"use client"

import { Trans } from "@lingui/react/macro"
import { cn } from "@rarible_int/anaconda_ui_components"
import Link from "next/link"
import { useState } from "react"
import { getFeaturedBlockchain } from "../blockchains/featured-blockchain/get-featured-blockchain"
import type { BlockchainFilter } from "../blockchains/filters/blockchain-filters-scope"
import { GetRari } from "../get-rari/get-rari"
import { useEvent } from "../hooks/use-event"

export function FeatureSection({
  blockchainFilter,
}: {
  blockchainFilter: BlockchainFilter
}) {
  const [openDialog, setOpenDialog] = useState(false)

  const handleGetRariClick = useEvent(() => {
    setOpenDialog(!openDialog)
  })

  const blockchain =
    blockchainFilter === "ALL" ? getFeaturedBlockchain() : blockchainFilter

  return (
    <div
      className={cn(
        "grid h-full gap-4",
        "grid-cols-1 grid-rows-3 sm:grid-cols-2 sm:grid-rows-2 md:grid-cols-2 md:grid-rows-2 lg:grid-cols-2 lg:grid-rows-2",
      )}
    >
      <Link
        className="sm:col-span-2 md:col-span-2 lg:col-span-2"
        href={`/${blockchain}/rewards`}
      >
        <FeatureCard
          title={<Trans>Earn Points</Trans>}
          className="h-full"
          style={{
            background:
              "linear-gradient(234deg, rgba(255, 44, 134, 0.10) 5.34%, rgba(255, 44, 134, 0.00) 76.46%)",
          }}
          iconClassName="group-hover:scale-105"
          icon={
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <title>Earn Points Icon</title>
              <path
                d="M0 4C0 1.79086 1.79086 0 4 0H36C38.2091 0 40 1.79086 40 4V36C40 38.2091 38.2091 40 36 40H4C1.79086 40 0 38.2091 0 36V4Z"
                fill="#FF2C86"
                fillOpacity="0.1"
              />
              <path
                d="M20.4869 12.2998C21.8068 12.5178 23.1583 13.1939 24.2076 14.4053L23.1441 15.4697C22.1784 14.3065 20.8766 13.7708 19.7066 13.7227C19.2138 13.7024 18.7752 13.7619 18.3951 13.8877C17.2276 14.2743 16.5661 15.3014 16.5045 16.3457C16.4442 17.3682 16.9564 18.3621 18.1559 18.7676C18.6583 18.9374 19.23 19.0477 19.8199 19.1133C21.3793 19.2865 23.3994 19.4683 24.6783 21.2588C25.458 22.3506 25.5587 23.5541 25.1803 24.623C24.8107 25.6668 24.0009 26.5471 23.0104 27.1133C22.2115 27.5698 21.3594 27.7929 20.4869 27.8262V29H18.9869V27.707C17.454 27.4211 16.15 26.6683 15.0807 25.5898L16.1402 24.5303C17.1487 25.5503 18.3687 26.1781 19.8063 26.3105C20.6939 26.3922 21.5175 26.2384 22.2662 25.8105C22.9977 25.3924 23.5356 24.7734 23.7662 24.1221C23.9878 23.4959 23.9413 22.8071 23.4576 22.1299C22.6265 20.9666 21.3383 20.7906 19.6549 20.6035C18.9944 20.5301 18.3099 20.4029 17.6754 20.1885C15.7651 19.5427 14.912 17.8775 15.0074 16.2578C15.1017 14.6599 16.118 13.0618 17.9234 12.4639C18.2631 12.3514 18.6192 12.279 18.9869 12.2441V11H20.4869V12.2998Z"
                fill="#FF2C86"
              />
            </svg>
          }
        />
      </Link>
      <GetRari
        dialogTriggerClassName="grid cursor-pointer"
        triggerButton={
          <FeatureCard
            title={<Trans>Get $RARI</Trans>}
            url=""
            className="grid"
            style={{
              background:
                "linear-gradient(234deg, rgba(250, 255, 0, 0.10) 5.34%, rgba(250, 255, 0, 0.00) 76.46%)",
            }}
            iconClassName="group-hover:scale-105 md:group-hover:scale-110"
            onClick={handleGetRariClick}
            icon={
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <title>Get $RARI Icon</title>
                <path
                  d="M0 4C0 1.79086 1.79086 0 4 0H36C38.2091 0 40 1.79086 40 4V36C40 38.2091 38.2091 40 36 40H4C1.79086 40 0 38.2091 0 36V4Z"
                  fill="#FAFF00"
                  fillOpacity="0.1"
                />
                <path
                  d="M13.5 21H23M13 26V14H14.5H23.5C25.1569 14 26.5 15.3431 26.5 17V18C26.5 19.6569 25.1569 21 23.5 21H23M23 21L26.4641 26"
                  stroke="#FAFF00"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            }
          />
        }
        openDialog={openDialog}
        onOpenChange={setOpenDialog}
      />
      <FeatureCard
        title={<Trans>Grow your brand</Trans>}
        url="https://rarible.org/"
        className=""
        style={{
          background:
            "linear-gradient(234deg, rgba(161, 249, 0, 0.10) 5.34%, rgba(161, 249, 0, 0.00) 76.46%)",
        }}
        iconClassName="group-hover:scale-105 md:group-hover:scale-110"
        icon={
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <title>Grow your brand Icon</title>
            <path
              d="M0 4C0 1.79086 1.79086 0 4 0H36C38.2091 0 40 1.79086 40 4V36C40 38.2091 38.2091 40 36 40H4C1.79086 40 0 38.2091 0 36V4Z"
              fill="#6CD42B"
              fillOpacity="0.1"
            />
            <path
              d="M9.31407 23.8897C8.97701 24.1305 8.89894 24.5989 9.1397 24.9359C9.38046 25.273 9.84887 25.3511 10.1859 25.1103L9.75 24.5L9.31407 23.8897ZM30.5 16C30.5 15.5858 30.1642 15.25 29.75 15.25H23C22.5858 15.25 22.25 15.5858 22.25 16C22.25 16.4142 22.5858 16.75 23 16.75H29V22.75C29 23.1642 29.3358 23.5 29.75 23.5C30.1642 23.5 30.5 23.1642 30.5 22.75V16ZM20.6922 23.048L21.1939 22.4905L20.6922 23.048ZM22.7563 22.9937L23.2866 23.524L22.7563 22.9937ZM9.75 24.5L10.1859 25.1103L16.2059 20.8104L15.7699 20.2001L15.334 19.5898L9.31407 23.8897L9.75 24.5ZM17.6452 20.3057L17.1435 20.8632L20.1905 23.6054L20.6922 23.048L21.1939 22.4905L18.147 19.7482L17.6452 20.3057ZM22.7563 22.9937L23.2866 23.524L30.2803 16.5303L29.75 16L29.2197 15.4697L22.226 22.4634L22.7563 22.9937ZM20.6922 23.048L20.1905 23.6054C21.08 24.406 22.4404 24.3702 23.2866 23.524L22.7563 22.9937L22.226 22.4634C21.9439 22.7454 21.4904 22.7574 21.1939 22.4905L20.6922 23.048ZM15.7699 20.2001L16.2059 20.8104C16.4921 20.6059 16.882 20.6278 17.1435 20.8632L17.6452 20.3057L18.147 19.7482C17.3625 19.0422 16.1928 18.9763 15.334 19.5898L15.7699 20.2001Z"
              fill="#A1F900"
            />
          </svg>
        }
      />
    </div>
  )
}

function FeatureCard({
  title,
  url,
  className,
  style,
  icon,
  iconClassName,
  onClick,
}: {
  title: React.ReactNode
  url?: string
  className?: string
  style?: React.CSSProperties
  icon?: React.ReactNode
  iconClassName?: string
  onClick?: () => void
}) {
  const cardContent = (
    <div
      className={cn(
        "flex w-full flex-row items-center justify-start gap-4 transition-transform md:flex-col md:items-start md:justify-between",
        iconClassName,
      )}
    >
      {icon && <div className="text-white/80">{icon}</div>}
      <h3 className="font-heading text-lg">{title}</h3>
    </div>
  )

  if (!url) {
    return (
      <div
        className={cn(
          "group flex min-h-[56px] items-end rounded-xl border border-white/20 p-3 md:min-h-[unset] md:p-6",
          className,
        )}
        style={style}
        onClick={onClick}
      >
        {cardContent}
      </div>
    )
  }

  return (
    <a
      className={cn(
        "group flex min-h-[56px] items-end rounded-xl border border-white/20 p-3 md:min-h-[unset] md:p-6",
        className,
      )}
      href={url}
      target="_blank"
      rel="noreferrer"
      style={style}
    >
      {cardContent}
    </a>
  )
}
